import React, { useState, useRef, useEffect } from 'react';
import { attachmentApi } from '../../services/api';

interface AttachmentUploadProps {
  expenseId?: string; // Optional - if provided, files will be uploaded immediately
  attachmentIds: string[];
  onChange: (attachmentIds: string[]) => void;
  onUploadComplete?: (uploadedAttachmentIds: string[]) => void; // Called when all pending uploads are complete with the uploaded IDs
  isEditing?: boolean; // Flag to indicate if we're editing an existing expense
}

interface PendingFile {
  file: File;
  id: string;
  name: string;
  size: number;
  type: string;
  error?: string;
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  uploading?: boolean;
  error?: string;
}

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const AttachmentUpload: React.FC<AttachmentUploadProps> = ({ expenseId, attachmentIds, onChange, onUploadComplete, isEditing = false }) => {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [hasCalledComplete, setHasCalledComplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // When expenseId is provided, upload all pending files
  useEffect(() => {
    console.log('[AttachmentUpload] useEffect triggered', {
      expenseId,
      pendingFilesCount: pendingFiles.length,
      pendingFiles: pendingFiles.map(f => f.name),
      isEditing,
      hasCalledComplete
    });
    
    // Don't auto-upload or call onUploadComplete if we're editing
    if (isEditing) {
      console.log('[AttachmentUpload] In editing mode, skipping auto-upload');
      return;
    }
    
    if (expenseId && !hasCalledComplete) {
      if (pendingFiles.length > 0) {
        console.log('[AttachmentUpload] Uploading pending files...');
        uploadPendingFiles();
      } else {
        console.log('[AttachmentUpload] No pending files, calling onUploadComplete immediately');
        // If no files to upload, call onUploadComplete immediately
        setHasCalledComplete(true);
        onUploadComplete?.([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseId]); // Only depend on expenseId, not pendingFiles.length
  
  const uploadPendingFiles = async () => {
    if (!expenseId) {
      console.log('[AttachmentUpload] uploadPendingFiles called but no expenseId');
      return;
    }
    
    if (pendingFiles.length === 0) {
      console.log('[AttachmentUpload] No pending files to upload');
      // Call onUploadComplete immediately if no files
      onUploadComplete?.([]);
      return;
    }
    
    console.log('[AttachmentUpload] Starting upload of', pendingFiles.length, 'files');
    
    const uploadedIds: string[] = [];
    for (const pendingFile of pendingFiles) {
      console.log('[AttachmentUpload] Uploading file:', pendingFile.name);
      const attachmentId = await uploadFile(pendingFile.file, expenseId);
      if (attachmentId) {
        uploadedIds.push(attachmentId);
      }
    }
    
    console.log('[AttachmentUpload] All files uploaded, clearing pending files. Uploaded IDs:', uploadedIds);
    // Clear pending files after upload
    setPendingFiles([]);
    
    // Notify parent that uploads are complete with the uploaded IDs
    onUploadComplete?.(uploadedIds);
  };
  
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };
  
  const validateFile = (file: File): string | null => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return 'Tipo de archivo no permitido. Solo se permiten PDF, JPG, PNG y Excel.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return `Archivo muy grande. Tamaño máximo: ${formatFileSize(MAX_FILE_SIZE)}`;
    }
    return null;
  };
  
  const uploadFile = async (file: File, expenseIdToUse: string): Promise<string | null> => {
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const newFile: UploadedFile = {
      id: tempId,
      name: file.name,
      size: file.size,
      type: file.type,
      uploading: true,
    };
    
    console.log('[AttachmentUpload] uploadFile starting for:', file.name, 'expenseId:', expenseIdToUse);
    setUploadedFiles(prev => [...prev, newFile]);
    
    try {
      console.log('[AttachmentUpload] Calling attachmentApi.upload...');
      const attachment = await attachmentApi.upload(file, expenseIdToUse);
      console.log('[AttachmentUpload] Upload successful:', attachment.attachmentId);
      
      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === tempId
            ? { ...f, id: attachment.attachmentId, uploading: false }
            : f
        )
      );
      
      onChange([...attachmentIds, attachment.attachmentId]);
      return attachment.attachmentId;
    } catch (error) {
      console.error('[AttachmentUpload] Upload failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error al subir archivo';
      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === tempId
            ? { ...f, uploading: false, error: errorMessage }
            : f
        )
      );
      return null;
    }
  };
  
  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList) return;
    
    console.log('[AttachmentUpload] handleFiles called with', fileList.length, 'files');
    const filesArray = Array.from(fileList);
    
    for (const file of filesArray) {
      const error = validateFile(file);
      
      if (error) {
        console.log('[AttachmentUpload] File validation failed:', file.name, error);
        const tempId = `error-${Date.now()}-${Math.random()}`;
        setPendingFiles(prev => [
          ...prev,
          {
            file,
            id: tempId,
            name: file.name,
            size: file.size,
            type: file.type,
            error,
          },
        ]);
      } else {
        // If expenseId is available, upload immediately
        if (expenseId) {
          console.log('[AttachmentUpload] expenseId available, uploading immediately:', file.name);
          await uploadFile(file, expenseId);
        } else {
          // Otherwise, store as pending
          console.log('[AttachmentUpload] No expenseId yet, storing as pending:', file.name);
          const tempId = `pending-${Date.now()}-${Math.random()}`;
          setPendingFiles(prev => [
            ...prev,
            {
              file,
              id: tempId,
              name: file.name,
              size: file.size,
              type: file.type,
            },
          ]);
        }
      }
    }
  };
  
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = e.dataTransfer.files;
    handleFiles(droppedFiles);
  };
  
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleRemoveFile = async (fileId: string) => {
    // Check if it's a pending file
    const pendingFile = pendingFiles.find(f => f.id === fileId);
    if (pendingFile) {
      setPendingFiles(prev => prev.filter(f => f.id !== fileId));
      return;
    }
    
    // Otherwise it's an uploaded file
    const file = uploadedFiles.find(f => f.id === fileId);
    if (!file) return;
    
    // Only delete from server if it was successfully uploaded
    if (!file.uploading && !file.error && !fileId.startsWith('temp-') && !fileId.startsWith('error-')) {
      try {
        await attachmentApi.delete(fileId);
        onChange(attachmentIds.filter(id => id !== fileId));
      } catch (error) {
        console.error('Error deleting attachment:', error);
      }
    }
    
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };
  
  const getFileIcon = (type: string): string => {
    if (type.startsWith('image/')) return '🖼️';
    if (type === 'application/pdf') return '📄';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    return '📎';
  };
  
  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
          onChange={handleFileInputChange}
          className="hidden"
        />
        
        <div className="space-y-2">
          <div className="text-4xl">📁</div>
          <div className="text-sm text-gray-600">
            <span className="font-medium text-blue-600">Haz clic para seleccionar</span> o arrastra archivos aquí
          </div>
          <div className="text-xs text-gray-500">
            PDF, JPG, PNG, Excel (máx. {formatFileSize(MAX_FILE_SIZE)})
          </div>
        </div>
      </div>
      
      {/* File list */}
      {(pendingFiles.length > 0 || uploadedFiles.length > 0) && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Archivos adjuntos</h4>
          <div className="space-y-2">
            {/* Pending files */}
            {pendingFiles.map((file) => (
              <div
                key={file.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  file.error
                    ? 'bg-red-50 border-red-200'
                    : 'bg-yellow-50 border-yellow-200'
                }`}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <span className="text-2xl flex-shrink-0">{getFileIcon(file.type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)} {!file.error && '(Pendiente de subir)'}
                    </p>
                    {file.error && (
                      <p className="text-xs text-red-600 mt-1">{file.error}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(file.id)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                    title="Eliminar archivo"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
            
            {/* Uploaded files */}
            {uploadedFiles.map((file) => (
              <div
                key={file.id}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  file.error
                    ? 'bg-red-50 border-red-200'
                    : file.uploading
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <span className="text-2xl flex-shrink-0">{getFileIcon(file.type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                    {file.error && (
                      <p className="text-xs text-red-600 mt-1">{file.error}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                  {file.uploading && (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  )}
                  {!file.uploading && (
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(file.id)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                      title="Eliminar archivo"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachmentUpload;
